import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import SingleSelect from '../../common/Forms/SingleSelect';

// WHAT DAY 0 IS for this offer: picture day, or the day each person was sent a
// particular email.
//
// A searchable select rather than a plain <select>, because the list is the
// whole Mailgun template catalogue -- a few hundred names that differ by a word
// (`fall_el_jd_plus_5` vs `fall_jd_plus_2_el`) -- and scrolling that to find one
// is how the wrong template gets picked. Matches the picker on the scheduled-job
// contact screen, which is the same choice over the same list.
//
// `creatable`, so a template that exists in Mailgun but has not been sent and is
// not yet in the catalogue can still be typed. Clearing it means picture day.
const OfferAnchorSelect = ({
  options, selectedValue, formName, catalogueUrls,
}) => {
  const [selected, setSelected] = useState(selectedValue);
  // Starts as the SEND HISTORY, which the server rendered -- so the control is
  // usable the instant the modal opens. The Mailgun catalogue is merged in
  // below when it arrives.
  const [choices, setChoices] = useState(options);
  // Why the catalogue is missing, when it is. Rendered -- see the note on the
  // warning below.
  const [catalogueError, setCatalogueError] = useState(null);
  // True when what arrived is Mailgun's first page, not the whole catalogue.
  const [cataloguePartial, setCataloguePartial] = useState(false);
  const hiddenInput = useRef(null);

  // The rest of the catalogue: templates Mailgun holds that nobody has been
  // sent yet. Fetched here rather than on the form's render because that render
  // is a modal open, and a Mailgun round trip does not belong in front of it --
  // the same reason MailgunInfo fetches this endpoint client-side.
  //
  // Failure does not empty the control -- it keeps the sent-history list it
  // already has -- but it is NOT silent. An unreadable catalogue and a domain
  // with no templates produce the same empty list, and telling them apart is
  // the whole difficulty: from 2026-10-09 every template dropdown in the admin
  // showed nothing because Mailgun was rejecting an out-of-range `limit`, and
  // for days everyone read that as "the template was never created". Say which
  // one it is.
  useEffect(() => {
    let live = true;

    if (catalogueUrls.length) {
      Promise.all(catalogueUrls.map((url) => axios.get(url)
        .then((res) => ({
          names: res.data.templates || [], error: res.data.error, partial: res.data.partial,
        }))
        .catch((e) => ({ names: [], error: e.message || 'request failed', partial: false }))))
        .then((results) => {
          if (!live) return;

          const failure = results.find((r) => r.error);
          setCatalogueError(failure ? failure.error : null);
          // A TRUNCATED list is its own warning. The fallback returns Mailgun's
          // first page only, so "my template isn't here" has the same cause and
          // the same dead end as an empty list -- it just looks like a working
          // control.
          setCataloguePartial(Boolean(failure && failure.partial));

          setChoices((current) => {
            const known = new Set(current.map((o) => o.value));
            const extra = [...new Set(results.flatMap((r) => r.names))]
              .filter((name) => name && !known.has(name))
              .sort()
              .map((name) => ({ label: `${name} — not sent yet`, value: name }));

            return extra.length ? [...current, ...extra] : current;
          });
        });
    }

    // The modal can be closed before the fetch lands.
    return () => { live = false; };
    // Joined, not the array itself: a prop array gets a fresh identity on every
    // render, and depending on it would refetch the catalogue in a loop.
  }, [catalogueUrls.join(',')]);

  const handleChange = (option) => {
    setSelected(option);

    // Tell the rest of the form the anchor moved, so the two day fields below
    // can relabel themselves "days after email" / "days after job". They live
    // outside this island and are handled by admin/coupons.js, which listens
    // for a plain bubbling `change` on this input -- exactly the event the
    // native <select> this replaced used to fire. Deferred a tick so React has
    // already written the new value by the time the listener reads it.
    window.setTimeout(() => {
      if (hiddenInput.current) {
        hiddenInput.current.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 0);
  };

  return (
    <div style={{ maxWidth: '420px' }}>
      <input
        name={formName}
        ref={hiddenInput}
        className="offer-anchor-select"
        value={selected ? selected.value : ''}
        readOnly
        hidden
      />
      <SingleSelect
        skipDefault
        creatable
        handleChange={handleChange}
        options={choices}
        selectedValue={selected}
        placeholderText="Picture day — each student's own shoot"
      />
      {catalogueError && (
        <p className="help-block" style={{ color: '#a94442', marginTop: '6px' }}>
          <strong>
            {cataloguePartial
              ? 'Mailgun template list incomplete'
              : 'Mailgun template list unavailable'}
          </strong>
          {' '}
          {cataloguePartial
            ? `— Mailgun refused the full request, so only its first page of
               templates is listed here. A template missing from this list still
               exists in Mailgun.`
            : `— showing only templates that have already been sent.`}
          {' '}
          Either way you can still use it: type its exact name and press Enter.
          <br />
          <small>{catalogueError}</small>
        </p>
      )}
    </div>
  );
};

const optionShape = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.string,
});

OfferAnchorSelect.propTypes = {
  options: PropTypes.arrayOf(optionShape),
  selectedValue: optionShape,
  formName: PropTypes.string,
  catalogueUrls: PropTypes.arrayOf(PropTypes.string),
};

OfferAnchorSelect.defaultProps = {
  options: [],
  selectedValue: null,
  formName: 'coupon[offer_anchor_template]',
  catalogueUrls: [],
};

export default OfferAnchorSelect;
